import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useSelection } from '../context/SelectionContext';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { useTabletMode } from '../context/TabletModeContext';
import { useTable } from './TableMenuWrapper';
import { useNavigate } from 'react-router-dom';
import { trackSelectionOpened } from '../lib/analytics';
import { ShoppingBag, RotateCcw, Sun, Moon } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RESET_COPY = {
  cat: { title: 'Comen\u00e7ar de nou?', body: 'S\u2019esborraran tots els productes seleccionats i tornar\u00e0s a la pantalla d\u2019idiomes.', confirm: 'S\u00ed, comen\u00e7ar de nou', cancel: 'Cancel\u00b7lar' },
  es: { title: '\u00bfEmpezar de nuevo?', body: 'Se borrar\u00e1n todos los productos seleccionados y volver\u00e1s a la pantalla de idiomas.', confirm: 'S\u00ed, empezar de nuevo', cancel: 'Cancelar' },
  en: { title: 'Start over?', body: 'All selected items will be cleared and you\u2019ll go back to the language screen.', confirm: 'Yes, start over', cancel: 'Cancel' },
  fr: { title: 'Recommencer\u00a0?', body: 'Tous les produits s\u00e9lectionn\u00e9s seront effac\u00e9s et vous reviendrez \u00e0 l\u2019\u00e9cran des langues.', confirm: 'Oui, recommencer', cancel: 'Annuler' },
};

export const TopCartBar = () => {
  const { isOpen, setIsOpen, getItemCount, lastAdded, getTotal, clearSelection } = useSelection();
  const { language, clearLanguage } = useLanguage();
  const { isDark, setDark } = useTheme();
  const { tableNumber } = useTable();
  const { isTablet } = useTabletMode();
  const navigate = useNavigate();
  const itemCount = getItemCount();
  const [hasActiveOrders, setHasActiveOrders] = useState(false);
  const [pulse, setPulse] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showResetHint, setShowResetHint] = useState(false);
  const copy = RESET_COPY[language] || RESET_COPY.es;

  // ── Sun Mode (shared with GuidedFlow via localStorage) ──
  // "Modo Sol" forces a bright, high-contrast palette for outdoor terrace use.
  // In Carta Libre we map it to the existing light theme so the menu becomes
  // legible under direct sunlight.
  const [sunMode, setSunMode] = useState(() => {
    try { return localStorage.getItem('pont10_sun_mode') === '1'; }
    catch { return false; }
  });
  const toggleSunMode = () => {
    const next = !sunMode;
    setSunMode(next);
    try { localStorage.setItem('pont10_sun_mode', next ? '1' : '0'); } catch {}
    // Sync the Carta Libre theme: Sun ON → light, Sun OFF → dark.
    // Called sequentially (NOT inside the functional updater) to avoid
    // React's "setState during render of another component" warning.
    try { setDark(!next); } catch {}
  };

  // Keep the Carta Libre theme in sync with the Sun Mode flag on mount —
  // useful when the user toggled it from inside the Guided Flow and then
  // navigated back to Carta Libre.
  useEffect(() => {
    try {
      const stored = localStorage.getItem('pont10_sun_mode') === '1';
      if (stored !== !isDark) setDark(!stored);
    } catch { /* silent */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // First-visit hint: show a soft tooltip on the reset button for 4 seconds,
  // then never again for this session.
  useEffect(() => {
    try {
      if (sessionStorage.getItem('reset_hint_shown')) return;
      const t1 = setTimeout(() => setShowResetHint(true), 1200);
      const t2 = setTimeout(() => {
        setShowResetHint(false);
        sessionStorage.setItem('reset_hint_shown', '1');
      }, 5500);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    } catch { /* silent */ }
  }, []);

  // Check for active orders
  useEffect(() => {
    if (!tableNumber) { setHasActiveOrders(false); return; }
    const checkOrders = async () => {
      try {
        const res = await fetch(`${API_URL}/api/mesa/${tableNumber}/session`);
        if (res.ok) {
          const data = await res.json();
          setHasActiveOrders(data.orders && data.orders.length > 0);
        }
      } catch (err) { /* silent */ }
    };
    checkOrders();
    const interval = setInterval(checkOrders, 30000);
    return () => clearInterval(interval);
  }, [tableNumber, isOpen]);

  // Pulse animation on item add
  useEffect(() => {
    if (lastAdded) {
      setPulse(true);
      const t = setTimeout(() => setPulse(false), 600);
      return () => clearTimeout(t);
    }
  }, [lastAdded]);

  const handleOpen = () => {
    trackSelectionOpened(itemCount, getTotal());
    setIsOpen(true);
  };

  const handleResetClick = () => setShowResetConfirm(true);

  const handleResetConfirm = async () => {
    try {
      if (clearSelection) await clearSelection();
    } catch (err) { /* silent */ }
    sessionStorage.removeItem('pont10_entry_choice');
    sessionStorage.removeItem('pont10_experience');
    if (clearLanguage) clearLanguage();
    setShowResetConfirm(false);
    setIsOpen(false);
    if (tableNumber) {
      navigate(`/mesa/${tableNumber}`, { replace: true });
    }
  };

  const shouldShowCart = itemCount > 0 || hasActiveOrders;

  const cartLabels = {
    es: 'Pedido', en: 'Order', cat: 'Comanda', fr: 'Commande'
  };
  const cartLabelsEmpty = {
    es: 'Pedidos', en: 'Orders', cat: 'Comandes', fr: 'Commandes'
  };

  return (
    <div
      data-testid="top-cart-bar"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        background: 'rgba(0,0,0,0.45)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        // Respect iOS safe area (status bar / notch). On tablet, also add
        // visual breathing room and larger inner padding.
        paddingTop: `calc(env(safe-area-inset-top, 0px) + ${isTablet ? '18px' : '10px'})`,
        paddingBottom: isTablet ? '14px' : '10px',
        paddingLeft: isTablet ? '24px' : '16px',
        paddingRight: isTablet ? '24px' : '16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      {/* Left: Reset + table + language */}
      <div style={{ display: 'flex', alignItems: 'center', gap: isTablet ? '14px' : '8px', flex: 1, minWidth: 0 }}>
        {/* Reset button — opens confirmation, then clears everything */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => {
              setShowResetHint(false);
              try { sessionStorage.setItem('reset_hint_shown', '1'); } catch {}
              handleResetClick();
            }}
            data-testid="topbar-reset-btn"
            aria-label="Empezar de nuevo"
            title="Empezar de nuevo"
            style={{
              background: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: '999px',
              padding: isTablet ? '10px 12px' : '7px 9px',
              minHeight: isTablet ? '44px' : '36px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer',
              color: 'rgba(255,255,255,0.75)',
              WebkitTapHighlightColor: 'transparent',
              transition: 'background 0.2s ease',
            }}
          >
            <RotateCcw size={isTablet ? 20 : 15} />
          </button>
          {showResetHint && (
            <div
              data-testid="reset-hint-tooltip"
              style={{
                position: 'absolute', top: 'calc(100% + 8px)', left: 0,
                background: 'rgba(20,18,15,0.95)',
                color: 'rgba(255,255,255,0.92)',
                fontFamily: "'DM Sans', sans-serif",
                fontSize: isTablet ? '14px' : '12px',
                fontWeight: 500,
                padding: '8px 12px', borderRadius: '10px',
                whiteSpace: 'nowrap',
                border: '1px solid rgba(200,155,60,0.3)',
                boxShadow: '0 6px 20px rgba(0,0,0,0.35)',
                pointerEvents: 'none',
                animation: 'fadeIn 0.25s ease',
                zIndex: 1,
              }}
            >
              {{
                es: 'Empezar de nuevo',
                en: 'Start over',
                cat: 'Començar de nou',
                fr: 'Recommencer',
              }[language] || 'Empezar de nuevo'}
            </div>
          )}
        </div>
        {/* Table badge */}
        {tableNumber && (
          <span
            data-testid="topbar-table-badge"
            style={{
              fontSize: isTablet ? '14px' : '10px',
              fontWeight: 600,
              color: 'rgba(255,255,255,0.55)',
              border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: '10px',
              padding: isTablet ? '4px 10px' : '2px 7px',
            }}
          >
            #{tableNumber}
          </span>
        )}
        {/* Sun Mode toggle — replaces former language button; provides quick
            access to high-contrast outdoor mode from anywhere in Carta Libre. */}
        <button
          onClick={toggleSunMode}
          data-testid="topbar-sun-mode-btn"
          aria-label={sunMode ? 'Modo noche' : 'Modo Sol'}
          title={sunMode ? 'Modo noche' : 'Modo Sol — pantalla clara para terraza'}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: isTablet ? '8px' : '4px',
            padding: isTablet ? '10px 16px' : '6px 10px',
            minHeight: isTablet ? '44px' : '36px',
            borderRadius: '18px',
            background: sunMode ? '#C89B3C' : 'rgba(255,255,255,0.08)',
            border: sunMode ? '1px solid #C89B3C' : '1px solid rgba(200,155,60,0.35)',
            cursor: 'pointer',
            fontSize: isTablet ? '15px' : '12px',
            fontWeight: 600,
            color: sunMode ? '#1A1612' : 'rgba(255,255,255,0.85)',
            transition: 'background 0.3s ease',
            letterSpacing: '0.04em',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          {sunMode ? <Moon size={isTablet ? 18 : 14} /> : <Sun size={isTablet ? 18 : 14} />}
          <span>{sunMode ? 'Noche' : 'Sol'}</span>
        </button>
      </div>

      {/* Center: PONT10 logo — fixed, decorative (no action) */}
      <div
        data-testid="topbar-pont10-logo"
        aria-hidden="true"
        style={{
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)',
          fontFamily: "'Playfair Display', serif",
          fontSize: isTablet ? '26px' : '18px',
          fontWeight: 600,
          letterSpacing: '0.5px',
          color: 'rgba(255,255,255,0.95)',
          lineHeight: 1,
          pointerEvents: 'none',
          userSelect: 'none',
          whiteSpace: 'nowrap',
        }}
      >
        PONT<span style={{ color: '#C89B3C', textShadow: '0 0 8px rgba(200,155,60,0.4)' }}>10</span>
      </div>

      {/* Right: Cart Button */}
      {shouldShowCart && !isOpen ? (
        <button
          onClick={handleOpen}
          data-testid="top-cart-button"
          className={pulse ? 'cart-bounce' : ''}
          style={{
            background: 'rgba(200,155,60,0.15)',
            border: '1px solid rgba(200,155,60,0.4)',
            borderRadius: '28px',
            padding: isTablet ? '14px 22px' : '12px 18px',
            minHeight: isTablet ? '50px' : '44px',
            fontSize: isTablet ? '18px' : '15px',
            display: 'flex',
            alignItems: 'center',
            gap: isTablet ? '10px' : '8px',
            color: 'rgba(255,255,255,0.9)',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            fontFamily: "'DM Sans', sans-serif",
            whiteSpace: 'nowrap',
          }}
        >
          <ShoppingBag size={isTablet ? 20 : 16} style={{ color: '#D4AF37' }} />
          <span style={{ fontWeight: 500 }}>
            {itemCount > 0
              ? `${cartLabels[language] || cartLabels.es} · ${itemCount}`
              : (cartLabelsEmpty[language] || cartLabelsEmpty.es)
            }
          </span>
          {itemCount === 0 && hasActiveOrders && (
            <span
              style={{
                width: '7px',
                height: '7px',
                borderRadius: '50%',
                backgroundColor: '#FFA000',
                animation: 'pulse 2s infinite',
              }}
            />
          )}
        </button>
      ) : (
        <div style={{ width: '20px' }} />
      )}

      {/* Reset confirmation modal — portaled to body for perfect centering, immune to topbar stacking */}
      {showResetConfirm && createPortal(
        <div
          data-testid="reset-confirm-modal"
          onClick={() => setShowResetConfirm(false)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99999,
            background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '20px',
            animation: 'fadeIn 0.18s ease',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: isDark ? '#1F1D1B' : '#FFFFFF',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
              borderRadius: '22px',
              padding: '32px 28px',
              maxWidth: '420px', width: '100%',
              boxShadow: '0 24px 70px rgba(0,0,0,0.5)',
              animation: 'popIn 0.22s cubic-bezier(0.16, 1, 0.3, 1)',
              textAlign: 'center',
            }}
          >
            <div style={{
              width: 56, height: 56, borderRadius: '50%',
              margin: '0 auto 16px',
              background: 'rgba(200,155,60,0.12)',
              border: '1px solid rgba(200,155,60,0.4)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <span style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: 22, fontWeight: 700, color: '#C89B3C',
              }}>10</span>
            </div>
            <h3 style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: '24px', fontWeight: 500,
              color: isDark ? '#F9F6F0' : '#1A1A1A',
              margin: '0 0 12px', lineHeight: 1.2,
            }}>
              {copy.title}
            </h3>
            <p style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '15px',
              color: isDark ? '#A39F98' : '#666',
              margin: '0 0 26px', lineHeight: 1.5,
            }}>
              {copy.body}
            </p>
            <div style={{ display: 'flex', gap: '10px', flexDirection: 'column' }}>
              <button
                onClick={handleResetConfirm}
                data-testid="reset-confirm-yes"
                style={{
                  width: '100%', padding: '14px',
                  borderRadius: '999px', border: 'none', cursor: 'pointer',
                  background: 'linear-gradient(135deg, #C89B3C, #D4AF37)',
                  color: '#1A1A1A', fontWeight: 600, fontSize: '15px',
                  fontFamily: "'DM Sans', sans-serif",
                  boxShadow: '0 4px 14px rgba(200,155,60,0.35)',
                }}
              >
                {copy.confirm}
              </button>
              <button
                onClick={() => setShowResetConfirm(false)}
                data-testid="reset-confirm-no"
                style={{
                  width: '100%', padding: '14px',
                  borderRadius: '999px',
                  border: `1px solid ${isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)'}`,
                  background: 'transparent',
                  color: isDark ? '#A39F98' : '#666',
                  fontWeight: 500, fontSize: '15px', cursor: 'pointer',
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                {copy.cancel}
              </button>
            </div>
          </div>
          <style>{`
            @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
            @keyframes popIn { from { opacity: 0; transform: scale(0.92) translateY(8px); } to { opacity: 1; transform: scale(1) translateY(0); } }
          `}</style>
        </div>,
        document.body
      )}
    </div>
  );
};

export default TopCartBar;
